import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { FiltersDropdown } from '@shared/components/ui/FiltersDropdown';
import { Search } from '@shared/components/ui/Search';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CartsFiltersPanel } from './CartsFiltersPanel';

interface FiltersSectionProps {
  handleSearch: (value: string) => void;
  onFilterChange?: (conditions: FilterConditionOutput[]) => void;
}

export const FiltersSection = ({ handleSearch, onFilterChange }: FiltersSectionProps) => {
  const { t } = useTranslation('cartsModule');

  const { params, updateParam } = useQueryParams({
    search: { type: 'string', default: '' },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
  });

  const [inputValue, setInputValue] = useState(params.search ?? '');

  const filtersFromUrl = useMemo(() => (Array.isArray(params.filters) ? params.filters : []), [params.filters]);

  useEffect(() => {
    const searchFromUrl = params.search ?? '';
    setInputValue((prev) => (prev !== searchFromUrl ? searchFromUrl : prev));
    handleSearch(searchFromUrl);
    onFilterChange?.(filtersFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.search, filtersFromUrl]);

  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      updateParam('filters', conditions);
      onFilterChange?.(conditions);
    },
    [updateParam, onFilterChange],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleSearchSubmit = useCallback(() => {
    const trimmedValue = inputValue.trim();
    updateParam('search', trimmedValue);
    handleSearch(trimmedValue);
  }, [inputValue, updateParam, handleSearch]);

  const activeFiltersCount = filtersFromUrl.length;

  return (
    <>
      <Search
        placeholder={t('search.placeholder')}
        value={inputValue}
        onChange={handleInputChange}
        onEnter={handleSearchSubmit}
        dataTestId="search"
        inputDataTestId="search-input"
      />
      <FiltersDropdown
        title={t('filter.filters', 'Filtros')}
        triggerLabel={t('filter.filters', 'Filtros')}
        activeFiltersCount={activeFiltersCount}
        triggerTestId="carts-filters-toggle"
      >
        <CartsFiltersPanel initialConditions={filtersFromUrl} onChange={handleFilterChange} />
      </FiltersDropdown>
    </>
  );
};
